import { gql } from '@apollo/client';
import { userServiceClient } from 'utils/graphQL/client/UserServiceClient';

export const GET_USER = gql`
  query GetUser($userId: String!) {
    getUser(id: $userId) {
      id
      createdAt
      updatedAt
      hashedId
      legacyUserId
      userType
      name
      email
      account
      legacyAccountId
      linesOfBusiness
      operatingMarkets
    }
  }
`;

export const GET_ACCESSORIAL_REQUEST_RULE = gql`
  query GetAccessorialRequestRule($query: AccessorialRequestRuleQueryInput!) {
    getAccessorialRequestRule(query: $query) {
      items {
        accessorial
        createdAt
        customer_approval_required
        user
        updatedAt
        user_type
        rules {
          id
          updatedAt
          value
        }
      }
      totalCount
      totalPages
    }
  }
`;

export const UPSERT_ACCESSORIAL_REQUEST_RULE = gql`
  mutation UpsertAccessorialRequestRule(
    $mutation: AccessorialRequestRuleMutationInput!
  ) {
    upsertAccessorialRequestRule(mutation: $mutation) {
      items {
        accessorial
        createdAt
        customer_approval_required
        id
        rules {
          createdAt
          id
          updatedAt
          value
        }
        updatedAt
        user
        user_type
      }
      page
      totalCount
      totalPages
    }
  }
`;

export const REMOVE_ACCESSORIAL_REQUEST_RULE = gql`
  mutation DeleteAccessorialRequestRule(
    $request: DeleteAccessorialRequestRulesInput!
  ) {
    deleteAccessorialRequestRule(request: $request)
  }
`;

export const GET_STOP_DOCUMENTS_CONFIG = gql`
  query GetStopDocumentsConfigQuery($query: StopDocumentsQueryInput!) {
    getStopDocumentsConfigQuery(query: $query) {
      stopConfigurations {
        id
        createdAt
        updatedAt
        movementType
        shipmentType
        actionType
        offsite
        empty
        stopDocuments
      }
    }
  }
`;

export const UPSERT_STOP_DOCUMENTS_CONFIG = gql`
  mutation UpsertStopDocumentsConfigMutation(
    $mutation: StopConfigurationsMutationInput!
  ) {
    upsertStopDocumentsConfigMutation(mutation: $mutation) {
      success
    }
  }
`;

export const GET_REFERENCE_NUMBER_REQUIREMENTS = gql`
  query GetReferenceNumberRequirementsQuery(
    $query: ReferenceNumberRequirementQueryInput!
  ) {
    getReferenceNumberRequirementsQuery(query: $query) {
      createdAt
      id
      reference_number {
        slug
      }
      required_at
      updatedAt
    }
  }
`;

export const UPSERT_REFERENCE_NUMBER_REQUIREMENTS = gql`
  mutation UpsertReferenceNumberRequirementsMutation(
    $mutation: ReferenceNumberRequirementsMutationInput!
  ) {
    upsertReferenceNumberRequirementsMutation(mutation: $mutation) {
      success
    }
  }
`;

export const GET_REFERENCE_NUMBERS_MASTER_LIST = gql`
  query Query($query: ReferenceNumberQueryInput!) {
    getReferenceNumbersQuery(query: $query) {
      reference_numbers {
        id
        slug
        viewable_by
        editable_by
      }
      count
      total
    }
  }
`;

export const REMOVE_REFERENCE_NUMBERS_FROM_MASTER_LIST = gql`
  mutation RemoveReferenceNumbersMutation($ids: [String!]!) {
    removeReferenceNumbersMutation(ids: $ids) {
      success
    }
  }
`;

export const UPSERT_REFERENCE_NUMBERS_TO_MASTER_LIST = gql`
  mutation Mutation($referenceNumbers: [ReferenceNumberInput!]!) {
    upsertReferenceNumbersMutation(referenceNumbers: $referenceNumbers) {
      id
      slug
      viewable_by
      editable_by
    }
  }
`;

export const GET_USER_INVOICE_SETTINGS = gql`
  query Invoice_setting($query: InvoiceSettingGetAndDeleteInput!) {
    getInvoiceSetting(query: $query) {
      invoice_setting {
        id
        user_id
        invoice_grouping
        invoice_delivery_configs {
          id
          charge_type
          invoicing_method
          send_edi
          send_email
          send_pdf
        }
      }
    }
  }
`;

export const MUTATE_USER_INVOICE_SETTINGS = gql`
  mutation UpsertInvoiceSetting($mutation: InvoiceSettingMutationInput!) {
    upsertInvoiceSetting(mutation: $mutation) {
      success
    }
  }
`;

export const GET_USER_BILL_READY_EVENTS = gql`
  query GetBillReadyEventsSetting(
    $query: BillReadyEventsSettingGetAndDeleteInput!
  ) {
    getBillReadyEventsSetting(query: $query) {
      id
      createdAt
      updatedAt
      user_id
      import_drayage_event
      import_rail_event
      export_drayage_event
      export_rail_event
      domestic_delivery_drayage_event
      domestic_delivery_rail_event
      domestic_pickup_drayage_event
      domestic_pickup_rail_event
      reposition_drayage_event
      reposition_rail_event
    }
  }
`;

export const MUTATE_USER_BILL_READY_EVENTS = gql`
  mutation UpsertBillReadyEventsSetting(
    $mutation: BillReadyEventsSettingMutationInput!
  ) {
    upsertBillReadyEventsSetting(mutation: $mutation) {
      success
    }
  }
`;

export const GET_USER_BILL_READY_STATUS = gql`
  query Query($userId: String!) {
    getBillReadyStatusCalculation(userId: $userId) {
      user_id
      include_pending_accessorials_in_calculation
    }
  }
`;

export const MUTATE_USER_BILL_READY_STATUS = gql`
  mutation upsertBillReadyStatusCalculation($mutation: UpdateBillingStatusCalculationInput!) {
    upsertBillReadyStatusCalculation(mutation: $mutation) {
      success
    }
  }
`;

export async function getPendingAccessorialInclusion(userId) {
  if (!userId) throw new Error('USER ID REQUIRED');
  return userServiceClient.query({
    query: GET_USER_BILL_READY_STATUS,
    variables: {
      userId,
    },
  });
}

export async function upsertPendingAccessorialInclusion(user_id, include_pending_accessorials_in_calculation) {
  return userServiceClient.mutate({
    mutation: MUTATE_USER_BILL_READY_STATUS,
    variables: {
      mutation: {
        user_id,
        include_pending_accessorials_in_calculation,
      },
    },
  });
}

export async function getUserBillReadyEvents(user_id) {
  if (!user_id) throw new Error('USER ID REQUIRED');
  return userServiceClient.query({
    query: GET_USER_BILL_READY_EVENTS,
    variables: {
      query: {
        user_id,
      },
    },
  });
}

export async function upsertUserBillReadyEvents(user_id, billReadyEvents) {
  // bill ready events keys must be the same name as expected by the user service apis (ex: import_drayage_event)
  return userServiceClient.mutate({
    mutation: MUTATE_USER_BILL_READY_EVENTS,
    variables: {
      mutation: {
        user_id,
        ...billReadyEvents,
      },
    },
  });
}

export async function getUserInvoiceSettings(user_id) {
  if (!user_id) throw new Error('USER ID REQUIRED');
  return userServiceClient.query({
    query: GET_USER_INVOICE_SETTINGS,
    variables: {
      query: {
        user_id,
      },
    },
  });
}

export async function upsertUserInvoiceSettings(user_id, mutation = {}) {
  const { invoice_grouping, invoice_delivery_configs } = mutation;
  return userServiceClient.mutate({
    mutation: MUTATE_USER_INVOICE_SETTINGS,
    variables: {
      mutation: {
        user_id,
        invoice_grouping,
        invoice_delivery_configs,
      },
    },
  });
}

export async function getUserMetadata(userId) {
  try {
    return await userServiceClient.query({
      query: GET_USER,
      variables: {
        userId,
      },
    });
  } catch (e) {
    console.error('Error during getUser: ', e.message);
    return { errors: [e] };
  }
}

export async function getAccessorialRequestRule(userId, options) {
  /*
  NOTE: Options can include: accessorials, page, per_page, count, and/or sort
  - accessorials is an array of strings
  - page and per_page are numbers
  - count is a boolean
  - sort is a string: ASC or DESC
  */
  return userServiceClient.query({
    query: GET_ACCESSORIAL_REQUEST_RULE,
    variables: {
      query: {
        user: userId,
        ...(Object.keys(options).length > 0 && options),
      },
    },
  });
}

export async function upsertAccessorialRequestRule(userId, userType, rules) {
  return userServiceClient.mutate({
    mutation: UPSERT_ACCESSORIAL_REQUEST_RULE,
    variables: {
      mutation: {
        user: userId,
        userType,
        rules,
      },
    },
  });
}

export async function removeAccessorialRequestRule(userId, accessorials) {
  return userServiceClient.mutate({
    mutation: REMOVE_ACCESSORIAL_REQUEST_RULE,
    variables: {
      request: {
        user: userId,
        accessorials,
      },
    },
  });
}

export async function getStopDocumentsConfig(shipperId) {
  return userServiceClient.query({
    query: GET_STOP_DOCUMENTS_CONFIG,
    variables: {
      query: { shipperId },
    },
  });
}

/*
  stopConfigurations is an array of configurations. Each config includes the following required properties:
    - actionType (string with defined enum values)
    - empty (bool)
    - offsite (bool)
    - movementType (string with defined enum values)
    - shipmentType (string with defined enum values)
    - stopDocuments (array with each object having a document property which is the doc name and a required property which is a bool)
*/
export async function upsertStopDocumentsConfig(shipperId, stopConfigurations) {
  return userServiceClient.mutate({
    mutation: UPSERT_STOP_DOCUMENTS_CONFIG,
    variables: {
      mutation: { shipperId, stopConfigurations },
    },
  });
}

export async function getReferenceNumberRequirements(userId, requiredAt) {
  // required_at is optional in the query
  return userServiceClient.query({
    query: GET_REFERENCE_NUMBER_REQUIREMENTS,
    variables: {
      query: {
        user: userId,
        ...(requiredAt && { required_at: requiredAt }),
      },
    },
  });
}

export async function upsertReferenceNumberRequirements(
  shipperId,
  requirements,
) {
  // requirements is an array of objects with reference_number (string) and required_at (enum) properties
  return userServiceClient.mutate({
    mutation: UPSERT_REFERENCE_NUMBER_REQUIREMENTS,
    variables: {
      mutation: { shipperId, requirements },
    },
  });
}

export function getReferenceNumbersMasterList(take, skip) {
  return userServiceClient.query({
    query: GET_REFERENCE_NUMBERS_MASTER_LIST,
    variables: {
      query: {
        options: {
          limit: take,
          skip,
        },
      },
    },
  });
}

export function removeReferenceNumberFromMasterList(id) {
  return userServiceClient.mutate({
    mutation: REMOVE_REFERENCE_NUMBERS_FROM_MASTER_LIST,
    variables: {
      ids: [id],
    },
  });
}

export function upsertReferenceNumberToMasterList(
  slug,
  viewableBy,
  editableBy,
  id = null,
) {
  return userServiceClient.mutate({
    mutation: UPSERT_REFERENCE_NUMBERS_TO_MASTER_LIST,
    variables: {
      referenceNumbers: [
        {
          id,
          slug,
          viewable_by: viewableBy,
          editable_by: editableBy,
        },
      ],
    },
  });
}
